"use client";

import { useEffect, useState } from "react";
import { Scissors } from "lucide-react";
import type { TrackPoint } from "@/lib/client-convert";
import { formatDuration } from "@/lib/units";
import { clamp, parseDuration, resolveTrim, type TrimRange } from "@/lib/trim";

function TimeField({
  label,
  seconds,
  onCommit,
}: {
  label: string;
  seconds: number;
  onCommit: (sec: number) => void;
}) {
  const [text, setText] = useState(() => formatDuration(seconds * 1000));

  // Keep the field in sync when the value changes elsewhere (slider, reset).
  useEffect(() => {
    setText(formatDuration(seconds * 1000));
  }, [seconds]);

  const commit = () => {
    const parsed = parseDuration(text);
    if (parsed === null) {
      setText(formatDuration(seconds * 1000)); // revert invalid input
      return;
    }
    onCommit(parsed);
  };

  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <input
        type="text"
        inputMode="numeric"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
            (e.target as HTMLInputElement).blur();
          }
        }}
        className="h-9 w-28 rounded-md border border-input bg-transparent px-3 py-1 font-mono text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    </label>
  );
}

export function TrimControl({
  points,
  value,
  onChange,
}: {
  points: TrackPoint[];
  value: TrimRange;
  onChange: (next: TrimRange) => void;
}) {
  const { totalSec, startSec, endSec, isTrimmed } = resolveTrim(points, value);
  if (totalSec < 1) return null;

  // Keep the handles >=1s apart so the result always has a real duration.
  const setStart = (sec: number) =>
    onChange({ startSec: clamp(Math.round(sec), 0, endSec - 1), endSec });
  const setEnd = (sec: number) =>
    onChange({ startSec, endSec: clamp(Math.round(sec), startSec + 1, totalSec) });

  const startPct = (startSec / totalSec) * 100;
  const endPct = (endSec / totalSec) * 100;

  return (
    <div className="rounded-xl p-6 glass-panel">
      <div className="flex items-center justify-between mb-5">
        <h4 className="font-bold flex items-center gap-2">
          <Scissors className="size-4 text-primary" />
          Trim activity
        </h4>
        {isTrimmed && (
          <button
            type="button"
            onClick={() => onChange({ startSec: 0, endSec: totalSec })}
            className="text-xs font-medium text-primary hover:underline"
          >
            Reset
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-4 mb-5">
        <TimeField label="Start" seconds={startSec} onCommit={setStart} />
        <TimeField label="End" seconds={endSec} onCommit={setEnd} />
        <p className="text-xs text-muted-foreground pb-2">
          h:mm:ss from the start &middot; total {formatDuration(totalSec * 1000)}
        </p>
      </div>

      <div className="trim-slider relative h-6 mb-3">
        <div className="absolute top-1/2 -translate-y-1/2 h-1.5 w-full rounded-full bg-border" />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-primary"
          style={{ left: `${startPct}%`, right: `${100 - endPct}%` }}
        />
        <input
          type="range"
          min={0}
          max={totalSec}
          value={startSec}
          onChange={(e) => setStart(Number(e.target.value))}
          aria-label="Trim start"
          className="trim-thumb"
        />
        <input
          type="range"
          min={0}
          max={totalSec}
          value={endSec}
          onChange={(e) => setEnd(Number(e.target.value))}
          aria-label="Trim end"
          className="trim-thumb"
        />
      </div>

      <p className="text-sm text-muted-foreground">
        Keeping{" "}
        <span className="font-medium text-foreground tabular-nums">
          {formatDuration((endSec - startSec) * 1000)}
        </span>{" "}
        of the activity.
      </p>

      <style jsx>{`
        .trim-thumb {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          margin: 0;
          background: transparent;
          pointer-events: none;
          -webkit-appearance: none;
          appearance: none;
        }
        .trim-thumb:focus {
          outline: none;
        }
        .trim-thumb::-webkit-slider-runnable-track {
          background: transparent;
          border: none;
        }
        .trim-thumb::-moz-range-track {
          background: transparent;
          border: none;
        }
        .trim-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          pointer-events: auto;
          height: 18px;
          width: 18px;
          border-radius: 9999px;
          background: hsl(var(--primary));
          border: 2px solid hsl(var(--background));
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
          cursor: grab;
        }
        .trim-thumb::-webkit-slider-thumb:active {
          cursor: grabbing;
        }
        .trim-thumb::-moz-range-thumb {
          pointer-events: auto;
          height: 18px;
          width: 18px;
          border-radius: 9999px;
          background: hsl(var(--primary));
          border: 2px solid hsl(var(--background));
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
          cursor: grab;
        }
      `}</style>
    </div>
  );
}
